import { TLSiteCartData } from '@trashlab/api/src/domains/consumer/types';
import {
  InformationStep,
  InformationStepSummary,
} from '@trashlab/sitesapp/src/organizations/redebins/components/shop/steps/information-step';
import {
  SelectUpsellItemsStep,
  SelectUpsellItemsStepSummary,
} from '@trashlab/sitesapp/src/organizations/redebins/components/shop/steps/select-upsell-items-step';
import {
  SelectDatesStep,
  SelectDatesStepSummary,
} from '@trashlab/sitesapp/src/organizations/redebins/components/shop/steps/select-dates-step';
import { OrderConfirmationStep } from '@trashlab/sitesapp/src/organizations/redebins/components/shop/steps/order-confirmation-step';
import { PaymentStep } from '@trashlab/sitesapp/src/components/shop/steps/payment-step';
import { ShopStepFrame } from '@trashlab/sitesapp/src/organizations/redebins/components/shop/shop-step-frame';
import { useMemo } from 'react';
import { trpcConsumerListUpsellItemsOpen } from '@trashlab/api/src/generated/hooks';
import {
  TLSiteShopStep,
  TLSiteShopStepsType,
} from '@trashlab/api/src/domains/consumer/types';
import React from 'react';

export interface StepComponentProps {
  cartData: TLSiteCartData;
}

export interface StepSummaryProps {
  cartData: TLSiteCartData;
}

export interface ShopStepConfig {
  key: TLSiteShopStepsType;
  label: string;
  render: (props: StepComponentProps) => React.ReactNode;
  renderSummary?: (props: StepSummaryProps) => React.ReactNode;
}

// Memoize step components to prevent unnecessary rerenders
const MemoizedInformationStep = React.memo(InformationStep);
const MemoizedInformationStepSummary = React.memo(InformationStepSummary);
const MemoizedSelectUpsellItemsStep = React.memo(SelectUpsellItemsStep);
const MemoizedSelectUpsellItemsStepSummary = React.memo(
  SelectUpsellItemsStepSummary,
);
const MemoizedSelectDatesStep = React.memo(SelectDatesStep);
const MemoizedSelectDatesStepSummary = React.memo(SelectDatesStepSummary);
const MemoizedPaymentStep = React.memo(PaymentStep);
const MemoizedOrderConfirmationStep = React.memo(OrderConfirmationStep);

// Create a static steps array
export const SHOP_STEPS: ShopStepConfig[] = [
  {
    key: TLSiteShopStep.Information,
    label: 'Information',
    render: ({ cartData }) => <MemoizedInformationStep cartData={cartData} />,
    renderSummary: ({ cartData }) => (
      <MemoizedInformationStepSummary cartData={cartData} />
    ),
  },
  {
    key: TLSiteShopStep.SelectUpsellItems,
    label: 'Select Upsell Items',
    render: ({ cartData }) => (
      <MemoizedSelectUpsellItemsStep cartData={cartData} />
    ),
    renderSummary: ({ cartData }) => (
      <MemoizedSelectUpsellItemsStepSummary cartData={cartData} />
    ),
  },
  {
    key: TLSiteShopStep.DeliveryAndPickup,
    label: 'Delivery & Pickup',
    render: ({ cartData }) => <MemoizedSelectDatesStep cartData={cartData} />,
    renderSummary: ({ cartData }) => (
      <MemoizedSelectDatesStepSummary cartData={cartData} />
    ),
  },
  {
    key: TLSiteShopStep.Payment,
    label: 'Payment',
    render: ({ cartData }) => (
      <MemoizedPaymentStep cartData={cartData} ShopStepFrame={ShopStepFrame} />
    ),
  },
  {
    key: TLSiteShopStep.Confirmation,
    label: 'Confirmation',
    render: ({ cartData }) => (
      <MemoizedOrderConfirmationStep cartData={cartData} />
    ),
  },
];

export function useShopSteps({ tenantId }: { tenantId: string }) {
  const { data: upsellItems } = trpcConsumerListUpsellItemsOpen.useQuery(
    {
      activeOnly: true,
      tenantId,
    },
    {
      enabled: !!tenantId,
    },
  );

  return useMemo(() => {
    if (!upsellItems?.length) {
      return SHOP_STEPS.filter(
        (step) => step.key !== TLSiteShopStep.SelectUpsellItems,
      );
    }
    return SHOP_STEPS;
  }, [upsellItems?.length]); // Only depend on the length of upsellItems
}
